import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FlagIcon from '@mui/icons-material/Flag';
import HistoryIcon from '@mui/icons-material/History';
import LanguageIcon from '@mui/icons-material/Language';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SyncIcon from '@mui/icons-material/Sync';
import CashSessionGate from './components/CashSessionGate';
import ProductSearch from './components/ProductSearch';
import CartPanel from './components/CartPanel';
import PaymentPanel from './components/PaymentPanel';
import ReturnFlow from './components/ReturnFlow';
import SessionSummary from './components/SessionSummary';
import { header } from '../components/header.encry';
import { createPosOrder, getPaymentMethods } from './api/posApi';
import { usePosState } from './state/usePosState';
import { PRODUCT_CACHE_UPDATED_EVENT, fetchProducts as fetchAllProducts } from '../components/ProductService';
import { fetchCategories } from '../components/ProductCategoryService';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;
const IMAGE_BASE_URL = process.env.REACT_APP_DATA_PIC_PATH || '';
const RESIZE_IMAGE_BASE_URL = IMAGE_BASE_URL ? `${IMAGE_BASE_URL.replace(/\/+$/, '')}/image.resize.php?path=` : '';

const getCategoryId = (product) => {
  const category = product?.category_id || product?.category;
  if (category && typeof category === 'object') {
    return String(category._id || category.id || category.value || '');
  }
  return String(category || '');
};

const getCategoryName = (product) => {
  const category = product?.category_id || product?.category;
  if (category && typeof category === 'object') {
    return category.name || category.label || '';
  }
  return '';
};

const resolveProductImageUrl = (rawImage) => {
  if (!rawImage) return '';
  const firstImage = Array.isArray(rawImage) ? rawImage[0] : rawImage;
  const value = String(firstImage || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  if (RESIZE_IMAGE_BASE_URL) return `${RESIZE_IMAGE_BASE_URL}${encodeURIComponent(value.replace(/^\/+/, ''))}`;
  if (!IMAGE_BASE_URL) return value;
  const base = IMAGE_BASE_URL.replace(/\/+$/, '');
  const normalized = value.replace(/^\/+/, '');
  return `${base}/${normalized}`;
};

const resolveCategoryImageUrl = (rawImage) => {
  if (!rawImage) return '';
  const firstImage = Array.isArray(rawImage) ? rawImage[0] : rawImage;
  const value = String(firstImage || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  const apiBase = (API_URL || '').replace(/\/+$/, '');
  if (!apiBase) return value;
  return `${apiBase}${value.startsWith('/') ? value : `/${value}`}`;
};

const parseProductPrice = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') return null;
  const price = Number(String(value).replace(',', '.'));
  return Number.isFinite(price) && price > 0 ? price : null;
};

const getPrice = (product) => {
  const candidates = [
    product?.discounted_price,
    product?.sale_price,
    product?.regular_price,
    product?.price,
    product?.prix,
    product?.selling_price,
    product?.price_ttc,
    product?.tarif,
  ];
  return candidates.map(parseProductPrice).find((value) => value !== null) || 0;
};

const normalizeProduct = (product) => ({
  ...product,
  _id: String(product?._id || product?.id || ''),
  name: product?.name || product?.title || product?.label || 'Produit sans nom',
  price: getPrice(product),
  image: resolveProductImageUrl(product?.url_product || product?.image || product?.imageUrl || product?.urlImage),
  category_id: getCategoryId(product),
  category_name: getCategoryName(product),
  sku: product?.sku || product?.ref || product?.reference || '',
  barcode: product?.barcode || product?.bar_code || '',
});

const normalizeCategory = (category) => ({
  _id: String(category?._id || category?.id || ''),
  name: category?.name || category?.label || 'Categorie',
  image: resolveCategoryImageUrl(category?.image || category?.url_image || category?.url_product || category?.src),
});

const normalizePayloadArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.response)) return payload.response;
  if (Array.isArray(payload?.products)) return payload.products;
  return [];
};

const getCashRegisterId = (register) => String(register?._id || register?.id || register?.value || '');

const getCashRegisterStoreId = (register) => String(
  register?.linked_store_id ||
  register?.store_id ||
  register?.storeId ||
  register?.default_store_id ||
  ''
);

const getCashRegisterLabel = (register) => (
  register?.label ||
  register?.name ||
  register?.account_name ||
  register?.title ||
  `Cash register ${getCashRegisterId(register)}`
);

const PosPage = () => {
  const defaultStoreId = sessionStorage.getItem('default_store_id') || '';
  const [session, setSession] = useState(null);
  const [cashRegisters, setCashRegisters] = useState([]);
  const [selectedCashRegister, setSelectedCashRegister] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedClientType, setSelectedClientType] = useState('personnal');
  const [productPreview, setProductPreview] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { hash } = useLocation();

  const {
    state,
    totals,
    addItem,
    updateQuantity,
    removeItem,
    addPayment,
    removePayment,
    clearCart,
  } = usePosState();

  const selectedCashRegisterRecord = useMemo(
    () => cashRegisters.find((register) => getCashRegisterId(register) === selectedCashRegister) || null,
    [cashRegisters, selectedCashRegister]
  );

  const storeId = getCashRegisterStoreId(selectedCashRegisterRecord) || defaultStoreId;

  const loadProducts = async () => {
    setProductsLoading(true);
    setProductsError('');
    try {
      let productList = await fetchAllProducts();
      if (!Array.isArray(productList) || productList.length === 0) {
        const paged = await axios.get(`${API_URL}/product/pages/60/1`, header());
        productList = normalizePayloadArray(paged?.data);
      }
      const normalized = productList
        .map(normalizeProduct)
        .filter((product) => product._id && product.name);
      setProducts(normalized);
    } catch (error) {
      setProductsError(error?.response?.data?.message || error.message || 'Impossible de charger les produits.');
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    const handleProductsCacheUpdated = (event) => {
      const freshProducts = Array.isArray(event?.detail?.products) ? event.detail.products : [];
      if (!freshProducts.length) return;
      const normalized = freshProducts
        .map(normalizeProduct)
        .filter((product) => product._id && product.name);
      setProducts(normalized);
    };

    window.addEventListener(PRODUCT_CACHE_UPDATED_EVENT, handleProductsCacheUpdated);
    return () => {
      window.removeEventListener(PRODUCT_CACHE_UPDATED_EVENT, handleProductsCacheUpdated);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadCashRegisters = async () => {
      try {
        const response = await axios.get(`${API_URL}/accounts`, {
          ...header(),
          params: { type: 'cash_register' },
        });
        const registers = normalizePayloadArray(response?.data).filter((register) => getCashRegisterId(register));
        if (!mounted) return;
        setCashRegisters(registers);
        if (registers.length > 0) {
          const defaultMatch = registers.find((register) => getCashRegisterStoreId(register) === defaultStoreId);
          setSelectedCashRegister(getCashRegisterId(defaultMatch || registers[0]));
        }
      } catch (error) {
        if (!mounted) return;
        setCashRegisters([]);
        setSelectedCashRegister('');
      }
    };
    loadCashRegisters();
    return () => {
      mounted = false;
    };
  }, [defaultStoreId]);

  useEffect(() => {
    let mounted = true;
    const loadCategories = async () => {
      try {
        const payload = await fetchCategories();
        const categories = normalizePayloadArray(payload).map(normalizeCategory);
        if (mounted) setProductCategories(categories.filter((category) => category._id));
      } catch (error) {
        if (mounted) setProductCategories([]);
      }
    };
    loadCategories();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!storeId) return;
    let mounted = true;
    const loadMethods = async () => {
      const data = await getPaymentMethods(storeId);
      if (mounted) setPaymentMethods(normalizePayloadArray(data));
    };
    loadMethods();
    return () => {
      mounted = false;
    };
  }, [storeId]);

  useEffect(() => {
    if (!hash) return;
    const element = document.querySelector(hash);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hash]);

  const categories = useMemo(() => {
    const counts = products.reduce((acc, product) => {
      if (!product.category_id) return acc;
      acc.set(product.category_id, (acc.get(product.category_id) || 0) + 1);
      return acc;
    }, new Map());

    const byId = new Map(productCategories.map((category) => [String(category._id), category.name]));
    products.forEach((product) => {
      if (product.category_id && product.category_name && !byId.has(product.category_id)) {
        byId.set(product.category_id, product.category_name);
      }
    });

    return Array.from(counts.entries())
      .map(([id, count]) => ({
        _id: id,
        name: byId.get(id) || 'Sans categorie',
        image: productCategories.find((category) => String(category._id) === String(id))?.image || '',
        count,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [productCategories, products]);

  const handleConfirmSale = async () => {
    if (!session || totals.remaining > 0 || state.items.length === 0) return;
    setOrderSubmitting(true);
    setSuccessMessage('');
    try {
      const result = await createPosOrder({
        store_id: storeId,
        cash_session_id: session._id,
        items: state.items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount || undefined,
        })),
        global_discount: state.globalDiscount || undefined,
        payments: state.payments.map((payment) => ({
          payment_method_id: payment.payment_method_id,
          amount: payment.amount,
          reference: payment.reference,
        })),
      });
      clearCart();
      setSearchTerm('');
      setSuccessMessage(`Vente enregistree ${result?.order?.orderCode ? `: ${result.order.orderCode}` : ''}`);
    } finally {
      setOrderSubmitting(false);
    }
  };

  return (
    <div className="pos-page" id="pos-cash-session">
      <style>{`
        .pos-page { min-height: 100vh; width: 100%; padding: 18px; background: radial-gradient(circle at top left, rgba(32, 140, 99, 0.16), transparent 34%), linear-gradient(135deg, #f7fbf7 0%, #eef5ef 48%, #fff7ed 100%); color: #10201a; }
        .pos-hero { display: flex; justify-content: space-between; gap: 18px; align-items: center; margin-bottom: 20px; padding: 22px; border-radius: 28px; background: linear-gradient(135deg, #0f3d2e, #1e6b4b); color: #fff; box-shadow: 0 24px 60px rgba(15, 61, 46, 0.22); }
        .pos-hero h2 { margin: 0; font-size: 30px; font-weight: 900; letter-spacing: -0.04em; }
        .pos-hero p { margin: 4px 0 0; opacity: 0.82; }
        .pos-hero-card { min-width: 190px; padding: 14px 16px; border: 1px solid rgba(255,255,255,0.2); border-radius: 20px; background: rgba(255,255,255,0.12); backdrop-filter: blur(12px); }
        .pos-hero-card span { display: block; font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.74; }
        .pos-hero-card strong { display: block; font-size: 22px; }
        .pos-layout { display: grid; grid-template-columns: minmax(0, 1fr) 420px; gap: 20px; align-items: start; }
        .pos-right-column { position: sticky; top: 18px; display: grid; gap: 16px; }
        .pos-products-shell, .pos-cart-card, .pos-payment-card, .pos-utility-card { background: rgba(255,255,255,0.86); border: 1px solid rgba(15, 61, 46, 0.08); border-radius: 26px; box-shadow: 0 18px 45px rgba(15, 61, 46, 0.09); }
        .pos-products-shell { padding: 18px; }
        .pos-products-toolbar, .pos-cart-header { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 16px; }
        .pos-eyebrow { font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em; color: #789184; font-weight: 800; }
        .pos-section-title { margin: 0; font-weight: 900; letter-spacing: -0.03em; }
        .pos-search-box { min-width: 330px; display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 16px; background: #f3f7f4; border: 1px solid #dfeae2; color: #456153; }
        .pos-search-box input { width: 100%; border: 0; outline: 0; background: transparent; font-weight: 700; color: #14241d; }
        .pos-category-strip { display: flex; gap: 10px; overflow-x: auto; padding: 2px 2px 16px; margin-bottom: 4px; }
        .pos-category-pill { flex: 0 0 auto; border: 1px solid #dce8df; background: #fff; border-radius: 999px; padding: 10px 14px; color: #254337; font-weight: 800; display: inline-flex; align-items: center; gap: 9px; }
        .pos-category-pill span { min-width: 24px; height: 24px; display: inline-grid; place-items: center; border-radius: 999px; background: #edf5ef; color: #2f674d; font-size: 12px; }
        .pos-category-pill.is-active { border-color: #1f7a55; background: #123f2f; color: #fff; }
        .pos-category-pill.is-active span { background: rgba(255,255,255,0.18); color: #fff; }
        .pos-products-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(178px, 1fr)); gap: 14px; max-height: calc(100vh - 255px); overflow: auto; padding-right: 4px; }
        .pos-product-card { text-align: left; border: 1px solid #e2ece5; border-radius: 22px; overflow: hidden; background: #fff; padding: 0; box-shadow: 0 12px 30px rgba(16, 32, 26, 0.07); transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease; }
        .pos-product-card:hover { transform: translateY(-3px); border-color: rgba(31, 122, 85, 0.35); box-shadow: 0 20px 42px rgba(16, 32, 26, 0.13); }
        .pos-product-image { position: relative; height: 145px; display: grid; place-items: center; background: linear-gradient(135deg, #eef6ef, #fff7ec); color: #9ca99f; }
        .pos-product-image img { width: 100%; height: 100%; object-fit: cover; }
        .pos-product-add { position: absolute; right: 10px; bottom: 10px; width: 36px; height: 36px; display: grid; place-items: center; border-radius: 999px; color: #fff; background: linear-gradient(135deg, #f59e0b, #15803d); box-shadow: 0 12px 24px rgba(21, 128, 61, 0.26); }
        .pos-product-body { padding: 13px; }
        .pos-product-name { min-height: 43px; font-size: 14px; font-weight: 900; line-height: 1.25; color: #16231d; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .pos-product-meta { margin-top: 8px; font-size: 12px; color: #7a8d82; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .pos-product-price { margin-top: 10px; color: #12633f; font-size: 18px; font-weight: 950; }
        .pos-cart-card, .pos-payment-card, .pos-utility-card { padding: 16px; }
        .pos-cart-count { display: inline-flex; align-items: center; gap: 8px; padding: 9px 12px; border-radius: 999px; color: #12633f; background: #eaf7ee; font-weight: 900; }
        .pos-cart-lines { display: grid; gap: 10px; max-height: 34vh; overflow: auto; }
        .pos-cart-line { padding: 12px; border-radius: 18px; background: #f8fbf8; border: 1px solid #e6eee8; }
        .pos-cart-line-main, .pos-cart-line-actions, .pos-total-box div, .pos-payment-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
        .pos-cart-line-name { font-weight: 900; line-height: 1.25; }
        .pos-cart-line-meta { margin-top: 3px; color: #718277; font-size: 12px; font-weight: 700; }
        .pos-icon-danger { border: 0; background: #fee2e2; color: #b91c1c; border-radius: 12px; width: 34px; height: 34px; display: grid; place-items: center; }
        .pos-cart-line-actions { margin-top: 12px; }
        .pos-qty-control { display: inline-flex; align-items: center; gap: 8px; padding: 5px; border-radius: 999px; background: #fff; border: 1px solid #dce8df; }
        .pos-qty-control button { width: 28px; height: 28px; border: 0; border-radius: 999px; background: #edf5ef; color: #184d38; display: grid; place-items: center; }
        .pos-qty-control span { min-width: 26px; text-align: center; font-weight: 900; }
        .pos-total-box { margin-top: 14px; padding-top: 14px; border-top: 1px solid #e2ece5; display: grid; gap: 8px; }
        .pos-total-box span { color: #6e8075; font-weight: 700; }
        .pos-total-box .is-total { margin-top: 6px; padding: 14px; border-radius: 18px; background: #123f2f; color: #fff; }
        .pos-total-box .is-total span { color: rgba(255,255,255,0.76); }
        .pos-total-box .is-total strong { font-size: 24px; }
        .pos-payment-due { display: flex; align-items: center; justify-content: space-between; padding: 14px; border-radius: 20px; background: linear-gradient(135deg, #fff7ed, #edf8ef); border: 1px solid #efe2ce; }
        .pos-payment-due span { color: #6e8075; font-weight: 800; }
        .pos-payment-due strong { color: #9a4d00; font-size: 22px; }
        .pos-method-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 14px; }
        .pos-method-button { border: 1px solid #dce8df; border-radius: 16px; background: #fff; padding: 12px; display: flex; align-items: center; justify-content: center; gap: 8px; font-weight: 900; color: #254337; }
        .pos-method-button.is-active { border-color: #1f7a55; color: #fff; background: linear-gradient(135deg, #176341, #23845b); }
        .pos-payment-add { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 10px; margin-top: 12px; }
        .pos-payment-add input { border: 1px solid #dce8df; border-radius: 16px; padding: 12px; outline: none; font-weight: 900; }
        .pos-payment-add button, .pos-confirm-button { border: 0; border-radius: 16px; padding: 12px 16px; font-weight: 950; }
        .pos-payment-add button { color: #fff; background: #123f2f; }
        .pos-payment-add button:disabled, .pos-confirm-button:disabled { opacity: 0.55; cursor: not-allowed; }
        .pos-payment-list { display: grid; gap: 8px; margin-top: 12px; }
        .pos-payment-row { padding: 10px 12px; background: #f8fbf8; border-radius: 14px; }
        .pos-payment-row div div { font-weight: 900; }
        .pos-payment-row span { color: #6e8075; font-size: 12px; font-weight: 800; }
        .pos-payment-row button { border: 0; background: transparent; color: #b91c1c; font-weight: 900; }
        .pos-confirm-button { width: 100%; margin-top: 14px; display: inline-flex; align-items: center; justify-content: center; gap: 8px; color: #fff; background: linear-gradient(135deg, #f59e0b, #15803d); box-shadow: 0 18px 32px rgba(21,128,61,0.18); }
        .pos-empty-state { padding: 32px; border-radius: 20px; text-align: center; color: #75877d; background: #f7fbf8; border: 1px dashed #d9e5dd; font-weight: 800; }
        .pos-empty-state.compact { padding: 18px; }
        .pos-status-message { margin-bottom: 14px; padding: 12px 14px; border-radius: 16px; font-weight: 800; }
        .pos-status-message.success { color: #12633f; background: #e8f7ee; }
        .pos-status-message.error { color: #991b1b; background: #fee2e2; }
        .pos-utility-card { margin-top: 16px; }
        @keyframes pos-cashier-switch-alert { 0%, 100% { background-color: #fff; border-color: #ef4444; color: #ef4444; } 50% { background-color: #ef4444; border-color: #ef4444; color: #fff; } }
        .pos-topbar { display: flex; flex-direction: column; gap: 14px; margin-bottom: 20px; padding: 16px; border-radius: 28px; background: rgba(255,255,255,0.92); border: 1px solid rgba(15, 61, 46, 0.08); box-shadow: 0 20px 50px rgba(15, 61, 46, 0.1); }
        .pos-topbar-row { display: flex; justify-content: space-between; gap: 16px; align-items: center; }
        .pos-register-group, .pos-platform-row { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
        .pos-register-select { min-width: 260px; border: 1px solid #dce8df; border-radius: 14px; padding: 10px 12px; font-weight: 800; background: #fff; color: #173b2c; }
        .pos-action-button { border: 1px solid #dce8df; border-radius: 999px; background: #fff; color: #254337; padding: 9px 13px; display: inline-flex; align-items: center; gap: 7px; font-weight: 900; box-shadow: 0 8px 18px rgba(15, 61, 46, 0.06); }
        .pos-action-button.primary { border-color: #1f7a55; background: #123f2f; color: #fff; }
        .pos-action-button.warning { border-color: #fde68a; background: #fffbeb; color: #92400e; }
        .pos-action-button.danger { border-color: #fecaca; background: #fff1f2; color: #b91c1c; }
        .pos-action-button.icon-only { width: 42px; height: 42px; justify-content: center; padding: 0; }
        .pos-platform-pill { border: 1px solid #dce8df; border-radius: 999px; background: #fff; padding: 7px 12px; display: inline-flex; align-items: center; gap: 9px; font-weight: 900; color: #203f32; }
        .pos-platform-logo { width: 30px; height: 30px; border-radius: 999px; display: inline-grid; place-items: center; background: #f3f7f4; color: #1f7a55; overflow: hidden; }
        .pos-switch { display: inline-flex; align-items: center; gap: 8px; border: 1px solid #dce8df; border-radius: 999px; background: #fff; padding: 5px 6px 5px 12px; font-size: 12px; font-weight: 900; color: #5f7468; }
        .pos-switch button { border: 0; border-radius: 999px; padding: 5px 10px; font-weight: 900; background: #e5e7eb; color: #475569; }
        .pos-switch button.is-on { background: #16a34a; color: #fff; }
        .pos-client-row { display: flex; flex-wrap: wrap; gap: 9px; margin: -4px 0 16px; }
        .pos-client-type { width: 68px; height: 56px; border: 1px solid #dce8df; border-radius: 16px; display: inline-flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; background: #fff; color: #111827; font-size: 10px; font-weight: 900; }
        .pos-client-type svg { font-size: 20px; }
        .pos-client-type.is-active { border-color: #f59e0b; background: #fde68a; color: #111827; box-shadow: 0 12px 22px rgba(245, 158, 11, 0.18); }
        .pos-menu-strip { display: flex; gap: 13px; overflow-x: auto; padding: 2px 2px 18px; margin-bottom: 4px; }
        .pos-menu-tile { width: 116px; flex: 0 0 116px; border: 0; background: transparent; color: #334155; display: flex; flex-direction: column; align-items: center; gap: 7px; font-weight: 900; }
        .pos-menu-circle { position: relative; width: 92px; height: 92px; border-radius: 999px; display: grid; place-items: center; overflow: hidden; border: 1px solid #dce8df; background: #fff; color: #1f7a55; font-size: 28px; box-shadow: 0 10px 25px rgba(15, 61, 46, 0.08); transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease; }
        .pos-menu-circle img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
        .pos-menu-fallback { display: grid; place-items: center; width: 100%; height: 100%; font-size: 28px; font-weight: 950; color: #0f766e; background: #f8fbf8; }
        .pos-menu-tile strong { width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 12px; }
        .pos-menu-tile small { margin-top: -5px; color: #64748b; font-weight: 800; }
        .pos-menu-tile.is-active { color: #12633f; }
        .pos-menu-tile.is-active .pos-menu-circle { border-color: #1f7a55; transform: translateY(-2px); box-shadow: 0 16px 30px rgba(31, 122, 85, 0.18), 0 0 0 4px rgba(31, 122, 85, 0.1); }
        .pos-product-preview { position: absolute; right: 10px; top: 10px; width: 34px; height: 34px; border: 0; display: grid; place-items: center; border-radius: 999px; color: #334155; background: rgba(255,255,255,0.92); box-shadow: 0 8px 16px rgba(15,23,42,0.16); }
        .pos-preview-backdrop { position: fixed; inset: 0; z-index: 2000; background: rgba(0,0,0,0.72); display: grid; place-items: center; padding: 24px; }
        .pos-preview-card { position: relative; max-width: min(92vw, 920px); max-height: 90vh; border-radius: 24px; overflow: hidden; background: #fff; box-shadow: 0 30px 80px rgba(0,0,0,0.35); }
        .pos-preview-card img { display: block; width: 100%; max-height: 86vh; object-fit: contain; }
        .pos-preview-close { position: absolute; top: 12px; right: 12px; border: 0; border-radius: 999px; width: 42px; height: 42px; background: rgba(255,255,255,0.9); font-weight: 900; }
        @media (max-width: 1180px) { .pos-layout { grid-template-columns: 1fr; } .pos-right-column { position: static; } .pos-products-grid { max-height: none; } }
        @media (max-width: 720px) { .pos-page { padding: 12px; } .pos-hero, .pos-products-toolbar, .pos-topbar-row { align-items: flex-start; flex-direction: column; } .pos-register-select, .pos-search-box { min-width: 0; width: 100%; } .pos-products-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .pos-method-grid { grid-template-columns: 1fr; } }
      `}</style>
      {productPreview && (
        <div className="pos-preview-backdrop" role="presentation" onClick={() => setProductPreview(null)}>
          <div className="pos-preview-card" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="pos-preview-close" onClick={() => setProductPreview(null)}>X</button>
            <img src={productPreview.image} alt={productPreview.name} />
          </div>
        </div>
      )}

      <CashSessionGate storeId={storeId} onSessionReady={setSession} hideHeader>
        <div className="pos-topbar">
          <div className="pos-topbar-row">
            <div className="pos-register-group">
              <select
                className="pos-register-select"
                value={selectedCashRegister}
                onChange={(event) => setSelectedCashRegister(event.target.value)}
                disabled={cashRegisters.length === 0}
              >
                {cashRegisters.length === 0 ? (
                  <option value="">{defaultStoreId ? `Cash register - store ${defaultStoreId}` : 'Select cash register'}</option>
                ) : (
                  cashRegisters.map((register) => {
                    const registerId = getCashRegisterId(register);
                    const registerStoreId = getCashRegisterStoreId(register);
                    return (
                      <option key={registerId} value={registerId}>
                        {getCashRegisterLabel(register)}{registerStoreId ? ` - store ${registerStoreId}` : ''}
                      </option>
                    );
                  })
                )}
              </select>
              <button type="button" className="pos-action-button primary" onClick={() => { clearCart(); setSuccessMessage('Nouvelle commande prete.'); }}>
                New Order
              </button>
              <button type="button" className="pos-action-button warning" disabled={state.items.length === 0}>
                <AccessTimeIcon fontSize="small" />
              </button>
              <button type="button" className="pos-action-button danger" disabled={state.items.length === 0} onClick={clearCart}>
                <DeleteOutlineIcon fontSize="small" />
              </button>
            </div>
            <div className="pos-platform-row">
              <button type="button" className="pos-action-button">
                <HistoryIcon fontSize="small" />
                Order history
              </button>
              <span className="pos-switch">
                Auto refresh
                <button type="button" className={autoRefreshEnabled ? 'is-on' : ''} onClick={() => setAutoRefreshEnabled((current) => !current)}>
                  {autoRefreshEnabled ? 'On' : 'Off'}
                </button>
              </span>
              <button type="button" className="pos-action-button icon-only" onClick={loadProducts} disabled={productsLoading}>
                <SyncIcon fontSize="small" />
              </button>
            </div>
          </div>
          <div className="pos-topbar-row">
            <div className="d-flex align-items-center gap-2">
              <StorefrontIcon />
              <strong>POS Le Celadon</strong>
              <span className="text-muted">Draft local: {state.items.length} article(s)</span>
            </div>
            <div className="pos-platform-row">
              {[
                { id: 'glovo', label: 'Glovo', count: 0, icon: LocalShippingIcon },
                { id: 'kooul', label: 'Kooul', count: 0, icon: LanguageIcon },
                { id: 'webapp', label: 'Website', count: selectedClientType === 'external' ? state.items.length : 0, icon: LanguageIcon },
              ].map((platform) => {
                const Icon = platform.icon;
                return (
                  <button type="button" key={platform.id} className="pos-platform-pill">
                    <span className="pos-platform-logo"><Icon fontSize="small" /></span>
                    <span>{platform.label}</span>
                    <strong>{platform.count}</strong>
                  </button>
                );
              })}
              <button type="button" className="pos-action-button icon-only" title="Cashier">
                <PersonIcon fontSize="small" />
              </button>
              <button type="button" className="pos-action-button icon-only" title="Cash session">
                <FlagIcon fontSize="small" color={session?._id ? 'success' : 'error'} />
              </button>
            </div>
          </div>
        </div>

        {successMessage && <div className="pos-status-message success">{successMessage}</div>}
        {productsError && <div className="pos-status-message error">{productsError}</div>}

        <div className="pos-layout">
          <ProductSearch
            products={products}
            categories={categories}
            selectedCategory={selectedCategory}
            selectedClientType={selectedClientType}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onCategoryChange={setSelectedCategory}
            onClientTypeChange={setSelectedClientType}
            onAdd={addItem}
            onPreview={setProductPreview}
            loading={productsLoading}
          />
          <div className="pos-right-column">
            <section id="pos-discount">
              <CartPanel
                items={state.items}
                totals={totals}
                onUpdateQty={(productId, quantity) => updateQuantity({ product_id: productId, quantity })}
                onRemove={(productId) => removeItem({ product_id: productId })}
              />
            </section>
            <section id="pos-payment-methods">
              <PaymentPanel
                methods={paymentMethods}
                totals={totals}
                onAddPayment={addPayment}
                payments={state.payments}
                onRemovePayment={removePayment}
                onConfirm={handleConfirmSale}
                disabled={orderSubmitting}
              />
            </section>
          </div>
        </div>

        <section id="pos-return-flow" className="pos-utility-card">
          <ReturnFlow />
        </section>
        <div className="pos-utility-card">
          <SessionSummary sessionId={session?._id} />
        </div>
      </CashSessionGate>
    </div>
  );
};

export default PosPage;
